import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/podkit/dropdown/DropDown";
import { DropdownActions } from "@/components/podkit/dropdown/DropDownActions";
import { useToast } from "@/components/podkit/toasts/use-toast";
import type { PlainWorkflowExecution } from "@/queries/automation-queries";
import { formatError } from "@/utils/errors";
import { useCallback, useState, type FC } from "react";
import { CancelWorkflowExecutionDialog } from "@/components/automations/CancelAutomationExecutionDialog";
import { canCancel } from "@/utils/automations";
import { IconEllipsisHorizontal } from "@/assets/icons/geist/IconEllipsisHorizontal";
import { Button } from "@/components/flexkit/Button";

export const WorkflowExecutionActionsDropdown: FC<{
    execution: PlainWorkflowExecution;
    onOpenChange?: (open: boolean) => void;
}> = ({ execution, onOpenChange }) => {
    const { toast } = useToast();
    const [showCancelModal, setShowCancelModal] = useState(false);

    const handleCopyId = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(execution.id);
            toast({
                title: "Execution ID copied to clipboard",
                description: execution.id,
            });
        } catch (error) {
            toast({
                title: "Failed to copy execution ID",
                description: formatError(error),
            });
        }
    }, [toast, execution.id]);

    const handleCancelClick = useCallback(() => setShowCancelModal(true), []);

    const showCancel = canCancel(execution);

    return (
        <div
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
            }}
            data-tracking-id-none
        >
            <DropdownActions 
                triggerTestId="workflow-execution-actions-dropdown-trigger" 
                button={
                    <Button 
                        variant="outline" 
                        aria-label="More actions" 
                        LeadingIcon={IconEllipsisHorizontal}
                    />
                }
                onOpenChange={onOpenChange}
            >
                <DropdownMenuItem
                    onClick={handleCopyId}
                    data-testid="workflow-execution-actions-dropdown-copy-id"
                    data-tracking-id="copy-id-workflow-execution-actions-dropdown"
                >
                    Copy Execution ID
                </DropdownMenuItem>
                {showCancel && (
                    <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            variant="destructive"
                            onClick={handleCancelClick}
                            data-testid="workflow-execution-actions-dropdown-cancel"
                            data-tracking-id="cancel-workflow-execution-actions-dropdown"
                        >
                            Cancel
                        </DropdownMenuItem>
                    </>
                )}
            </DropdownActions>
            <CancelWorkflowExecutionDialog
                open={showCancelModal}
                execution={execution}
                onClose={() => setShowCancelModal(false)}
            />
        </div>
    );
};
